import { useEffect, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import Layout from "@/components/Layout";
import SEO from "@/components/SEO";
import { Eyebrow } from "@/components/Eyebrow";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { CheckCircle2, Loader2 } from "lucide-react";

const MAX_ATTEMPTS = 5;
const POLL_INTERVAL_MS = 2000;

export default function CheckoutSuccess() {
  const [searchParams] = useSearchParams();
  const { user, refresh } = useAuth();
  const sessionId = searchParams.get("session_id");
  const [status, setStatus] = useState({ phase: "checking", data: null, error: null });
  const attemptsRef = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setStatus({ phase: "error", error: "Missing session_id", data: null });
      return;
    }
    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      try {
        const res = await api.get(`/checkout/status/${sessionId}`);
        const data = res.data;
        if (data.payment_status === "paid") {
          setStatus({ phase: "paid", data, error: null });
          refresh();
          return;
        }
        if (data.status === "expired") {
          setStatus({ phase: "expired", data, error: null });
          return;
        }
        attemptsRef.current += 1;
        if (attemptsRef.current >= MAX_ATTEMPTS) {
          setStatus({ phase: "timeout", data, error: null });
          return;
        }
        setStatus({ phase: "pending", data, error: null });
        setTimeout(poll, POLL_INTERVAL_MS);
      } catch (e) {
        setStatus({ phase: "error", error: "Status check failed", data: null });
      }
    };
    poll();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  const isLoading = status.phase === "checking" || status.phase === "pending";

  return (
    <Layout>
      <SEO noindex title="Checkout — Beyond Valencia" canonical="/checkout/success" />
      <section className="max-w-2xl mx-auto px-5 sm:px-8 py-20 lg:py-28" data-testid="checkout-success-page">
        <div className="border border-stone/40 bg-bone p-10 sm:p-14 text-center">
          {status.phase === "paid" ? (
            <>
              <CheckCircle2 className="h-12 w-12 text-ivy mx-auto" />
              <Eyebrow className="mt-6 inline-block">Welcome aboard</Eyebrow>
              <h1 className="font-serif font-medium text-4xl sm:text-5xl text-ink mt-3 leading-tight tracking-tight">
                Welcome to Beyond Valencia.
              </h1>
              <p className="text-ink-soft mt-5 max-w-md mx-auto leading-relaxed">
                Your tier is provisioned. We'll be in touch within 1 business day to schedule your
                first session.
              </p>
              <div className="mt-8 flex flex-wrap justify-center gap-3">
                {user ? (
                  <Link to="/dashboard">
                    <span
                      className="inline-block bg-ivy hover:bg-ivy-light text-bone uppercase tracking-[0.18em] text-xs font-semibold py-4 px-7 transition-colors"
                      data-testid="success-go-dashboard"
                    >
                      Go to dashboard
                    </span>
                  </Link>
                ) : (
                  <Link to="/login">
                    <span
                      className="inline-block bg-ivy hover:bg-ivy-light text-bone uppercase tracking-[0.18em] text-xs font-semibold py-4 px-7 transition-colors"
                      data-testid="success-sign-in"
                    >
                      Sign in to access your portal
                    </span>
                  </Link>
                )}
                <Link to="/story">
                  <span className="inline-block border border-stone/50 hover:border-ink text-ink uppercase tracking-[0.18em] text-xs font-semibold py-4 px-6 transition-colors">
                    Read the story
                  </span>
                </Link>
              </div>
            </>
          ) : isLoading ? (
            <>
              <Loader2 className="h-10 w-10 text-ivy mx-auto animate-spin" />
              <h1 className="font-serif font-medium text-3xl text-ink mt-6 tracking-tight">Verifying your payment…</h1>
              <p className="text-ink-soft text-sm mt-3 uppercase tracking-[0.18em]" data-testid="checkout-status-phase">
                Phase: <span className="font-mono normal-case tracking-normal">{status.phase}</span>
              </p>
            </>
          ) : (
            <>
              <h1 className="font-serif font-medium text-3xl text-ink tracking-tight">
                {status.phase === "expired" ? "Checkout session expired" :
                 status.phase === "timeout" ? "Still processing — check your email" :
                 "Something went wrong"}
              </h1>
              <p className="text-ink-soft text-sm mt-3 uppercase tracking-[0.18em]" data-testid="checkout-status-phase">
                Phase: <span className="font-mono normal-case tracking-normal">{status.phase}</span>
              </p>
              {status.error && <p className="text-crimson text-sm mt-3">{status.error}</p>}
              <div className="mt-8 flex flex-wrap justify-center gap-3">
                <Link to="/services">
                  <span className="inline-block border border-stone/50 hover:border-ink text-ink uppercase tracking-[0.18em] text-xs font-semibold py-4 px-6 transition-colors">
                    Back to services
                  </span>
                </Link>
                <Link to="/contact">
                  <span className="inline-block bg-ivy hover:bg-ivy-light text-bone uppercase tracking-[0.18em] text-xs font-semibold py-4 px-6 transition-colors">
                    Contact us
                  </span>
                </Link>
              </div>
            </>
          )}

          {status.data && (
            <pre className="text-[11px] bg-bone-dim border border-stone/30 p-4 overflow-auto max-h-40 text-left mt-10 font-mono" data-testid="checkout-status-json">
              {JSON.stringify(status.data, null, 2)}
            </pre>
          )}
        </div>
      </section>
    </Layout>
  );
}
